import type { PerguntaRespondida } from '@/api/secretario';
import { Badge } from '@/components/ui/badge';

/**
 * O questionário da área e o que o cliente respondeu — inclusive o que ficou sem
 * resposta registrada.
 *
 * Existe por causa do atendimento real de 2026-08-21. O caso fechou inteiro, mas
 * o advogado o recebeu sem o CNPJ da empresa, sem saber como o contrato terminou
 * e sem testemunha — num vínculo SEM registro em carteira, em que a testemunha é
 * a prova principal. Nenhuma das três travava o atendimento, e pergunta que não
 * trava só é feita se a conversa abrir espaço.
 *
 * A saída NÃO foi mandar o agente perguntar tudo: o harness já mediu que é o
 * volume de perguntas que faz a pessoa ir embora antes de assinar (3 conversas
 * em 16). Então o que não foi perguntado não vira mais turno de WhatsApp — vira
 * esta lista, onde o advogado vê o buraco e decide se vale a pena buscar.
 *
 * Por isso a seção mostra as respondidas TAMBÉM: ler "sem registro" solto não
 * diz se a entrevista foi rasa ou se aquele item é dispensável no caso.
 */
export function QuestionarioDaConversa({ questionario }: { questionario: PerguntaRespondida[] }) {
  if (questionario.length === 0) return null;

  const respondidas = questionario.filter((p) => temResposta(p)).length;
  // Só conta como buraco o que o próprio gate cobraria: obrigatória SEM condição.
  // Contar as outras marcaria como falha justamente o que o desenho deixa passar
  // — e a única pergunta obrigatória COM condição do catálogo ("há menor de idade
  // envolvido?") apareceria como alarme em toda conversa sem menor nenhum.
  const emAberto = questionario.filter((p) => !temResposta(p) && !p.removida && bloqueava(p)).length;

  return (
    <section className="space-y-2 border-t pt-3">
      <div>
        <h3 className="text-sm font-medium">O caso, pelo questionário da área</h3>
        <p className="text-xs text-muted-foreground">{resumo(respondidas, emAberto, questionario.length)}</p>
      </div>
      <ul className="space-y-2">
        {questionario.map((pergunta, indice) => (
          <PerguntaItem key={`${indice}-${pergunta.texto}`} pergunta={pergunta} />
        ))}
      </ul>
    </section>
  );
}

function temResposta(pergunta: PerguntaRespondida): boolean {
  return (pergunta.resposta ?? '').trim() !== '';
}

/** Travava o atendimento? Mesmo critério do gate: obrigatória e sem condição. */
function bloqueava(pergunta: PerguntaRespondida): boolean {
  return pergunta.obrigatoria && (pergunta.condicao ?? '') === '';
}

/**
 * O subtítulo NÃO afirma por que a resposta falta.
 *
 * O dado diz apenas que nada foi registrado, e há caminho em que a pergunta foi
 * feita e a resposta existe na transcrição: o cliente que responde "prefiro não
 * dizer" a uma pergunta Sim/Não tem a recusa acolhida e nada gravado. Afirmar
 * "ninguém perguntou" faria o advogado ligar para o cliente e repetir a pergunta
 * que ele já se recusou a responder.
 */
function resumo(respondidas: number, emAberto: number, total: number): string {
  if (respondidas === 0) {
    return 'A entrevista não chegou a acontecer nesta conversa.';
  }
  const semRegistro = total - respondidas;
  if (semRegistro === 0) {
    return 'Todas as perguntas do questionário têm resposta registrada.';
  }
  const abertas =
    emAberto > 0 ? ` ${emAberto === 1 ? 'Uma delas é obrigatória' : `${emAberto} delas são obrigatórias`}.` : '';
  return `${semRegistro} ${semRegistro === 1 ? 'pergunta ficou' : 'perguntas ficaram'} sem resposta registrada.${abertas}`;
}

function PerguntaItem({ pergunta }: { pergunta: PerguntaRespondida }) {
  const resposta = (pergunta.resposta ?? '').trim();

  if (pergunta.removida) {
    return (
      <li className="rounded-lg border p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <p className="text-sm font-medium text-muted-foreground">Pergunta removida do questionário</p>
          <Badge variant="outline" className="shrink-0 whitespace-nowrap">
            Resposta antiga
          </Badge>
        </div>
        <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{resposta}</p>
      </li>
    );
  }

  return (
    <li className="rounded-lg border p-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <p className="text-sm font-medium">{pergunta.texto}</p>
        {resposta === '' && (
          // Rótulo do que se SABE ("não há registro"), nunca da causa. E o que
          // travava vem marcado, porque é o que o advogado precisa olhar duas
          // vezes: o gate não deixaria passar, então ou foi dispensado ou o
          // atendimento parou ali.
          <Badge
            variant={bloqueava(pergunta) ? 'destructive' : 'outline'}
            className="shrink-0 whitespace-nowrap"
          >
            {bloqueava(pergunta) ? 'Obrigatória, sem resposta' : 'Sem resposta'}
          </Badge>
        )}
      </div>
      {(pergunta.condicao ?? '') !== '' && (
        <p className="mt-1 text-xs text-muted-foreground">Feita só {pergunta.condicao}.</p>
      )}
      {resposta !== '' && <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{resposta}</p>}
    </li>
  );
}
